import { PlusIcon } from "vega-icons";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import {
  CardVariant,
  EmptyCard,
} from "@vega-core/components/Cards/EmptyCard/EmptyCard";
import { Skeleton } from "@vega-core/components/shadcn/skeleton";
import { CoreTable } from "@vega-core/components/Table/CoreTable/CoreTable";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";

import { type useSingleCollaboratorRolesManager } from "../hooks/useSingleCollaboratorRolesManager";

import { CreateRoleAccordion } from "./CreateRoleAccordion";

type CollaboratorsSideDrawerRolesV2Props = {
  isEditing: boolean;
  manager: Pick<
    ReturnType<typeof useSingleCollaboratorRolesManager>,
    | "addRole"
    | "collaboratorHasNoPermissions"
    | "columns"
    | "getDisabledRoleIdsForTab"
    | "isLoadingRoles"
    | "permissionsData"
    | "roles"
  >;
};

export const CollaboratorsSideDrawerRolesV2 = ({
  isEditing,
  manager,
}: CollaboratorsSideDrawerRolesV2Props) => {
  const {
    collaboratorHasNoPermissions,
    permissionsData,
    columns,
    getDisabledRoleIdsForTab,
    isLoadingRoles,
    addRole,
    roles,
  } = manager;

  const {
    isOpen: isCreating,
    setTrue: openCreating,
    setFalse: closeCreating,
  } = useBoolean(false);

  if (isLoadingRoles) {
    return (
      <div className="flex flex-col gap-4 pb-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <Skeleton className="h-5 w-48" />
          </div>
          <div className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <Skeleton className="h-5 w-48" />
          </div>
        </div>
      </div>
    );
  }

  if (collaboratorHasNoPermissions && !isEditing) {
    return (
      <EmptyCard
        description={m.common_noResultsFound()}
        variant={CardVariant.NoResults}
      />
    );
  }

  const renderCreateRoleSection = () => {
    if (!isEditing) return null;

    if (isCreating) {
      return (
        <CreateRoleAccordion
          getDisabledRoleIdsForTab={getDisabledRoleIdsForTab}
          onAdd={(roleId, fundVehicleTab) => {
            addRole(roleId, fundVehicleTab);
            closeCreating();
          }}
          onCancel={closeCreating}
          roles={roles}
        />
      );
    }

    return (
      <div className="w-full border-2 border-dashed border-border/primary rounded-small flex items-center justify-center py-2">
        <ThemedButton
          dataTestId="create-new-role-button"
          icon={<PlusIcon className="w-4 h-4" />}
          iconPosition="right"
          onClick={openCreating}
          type="button"
          variant="tertiary"
        >
          {m.settings_collaborators_createNewRole()}
        </ThemedButton>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4 pb-6">
      {renderCreateRoleSection()}
      {permissionsData.length > 0 && (
        <CoreTable
          columns={columns}
          data={permissionsData}
          dataTestId="collaborators-roles-table"
          noSorting
        />
      )}
    </div>
  );
};
